'use client'

import {
  Badge,
  Button,
  Card,
  Col,
  Icon,
  type IconNames,
  NumberUnit,
  Row,
  Typography,
} from '@repo/ui/components'
import { type MessagesKeys, useTranslate } from '@repo/shared/hooks'
import { Link, useRouter, usePathname } from '@repo/shared/i18n/navigation'

import {
  useUserAuthDetails,
  useGetLoyaltyPortfolio,
  useLogout,
} from '@repo/services'
import { cn } from '@repo/ui/lib/utils'
import { Cookie } from '@repo/tools'
import Image from 'next/image'
import { ValueWithUnit } from '@repo/shared/index'

type MenuItem = {
  label: MessagesKeys
  icon: IconNames
  activeIcon?: IconNames
  badge?: {
    text: MessagesKeys
    variant: 'liquid' | 'solid'
    count?: number
    value?: number
    color?: 'negative' | 'positive' | 'neutral' | 'alert'
  }
  href: string
  disabled: boolean
}

type SettingsItem = {
  label: MessagesKeys
  icon: IconNames
  href: string
  disabled: boolean
  onClick?: () => void
}

export const ProfileSidebarLayout = () => {
  const { data: authDetails, isPending } = useUserAuthDetails()
  const { data: { data: { portfolio = {} } = {} } = {} } =
    useGetLoyaltyPortfolio()

  const { push } = useRouter()

  const accountItems: MenuItem[] = [
    {
      label: 'profileLayout_account',
      icon: 'Person',
      href: '/profile/account',
      badge:
        !authDetails?.data?.details?.kyc && !isPending
          ? {
              text: 'profileLayout_complete_kyc',
              variant: 'solid',
              color: 'alert',
            }
          : undefined,
      disabled: false,
    },

    {
      label: 'profileLayout_feeLevel',
      icon: 'Percentage',
      href: '/profile/fee',
      disabled: false,
    },
  ]

  const menuItems: MenuItem[] = [
    // {
    //   label: 'profileLayout_identityVerification',
    //   icon: 'UndefinedGhostLine',
    //   badge: {
    //     text: 'profileLayout_level',
    //     variant: 'liquid',
    //     value: authDetails?.data?.details?.kyc?.level,
    //   },
    //   href: '/profile/identity-verification',
    //   disabled: true,
    // },

    {
      label: 'profileLayout_messages',
      icon: 'EnvelopeClose',
      badge: { text: 'profileLayout_newMessages', variant: 'solid', count: 0 },
      href: '/profile/messages',
      disabled: false,
    },
    {
      label: 'profileLayout_cardsAndAccounts',
      icon: 'CardLine',
      href: '/profile/cards',
      disabled: false,
    },
  ]

  const settingsItems: SettingsItem[] = [
    // {
    //   label: 'profileLayout_security',
    //   icon: 'UndefinedGhostLine',
    //   href: '/profile/security',
    //   disabled: true,
    // },
    {
      label: 'profileLayout_password',
      icon: 'KeyLine',
      href: '/profile/password',
      disabled: false,
    },
    {
      label: 'profileLayout_settings',
      icon: 'Gear',
      href: '/profile/settings',
      disabled: false,
    },
  ]

  const supportItems: SettingsItem[] = [
    {
      label: 'profileLayout_support',
      icon: 'Headset',
      href: '/profile/support',
      disabled: false,
      onClick: () => {
        // @ts-ignore
        Goftino.open()
      },
    },
  ]

  const { t } = useTranslate()
  const pathname = usePathname()
  const { clearCache } = useLogout()

  const handleLogout = () => {
    Cookie.removeAuthTokens()
    clearCache()
    if (typeof window !== 'undefined') {
      window.location.href = '/auth/login'
    } else {
      push('/auth/login')
    }
  }

  return (
    <Col className='gap-md mb-20 sm:mb-0 md:px-md'>
      <Card className='!p-0 '>
        <Col className='gap-md items-center pt-md '>
          <Typography size='sm' weight='bold'>
            {authDetails?.data?.details?.firstName}{' '}
            {authDetails?.data?.details?.lastName}
          </Typography>
          <Badge
            variant='liquid'
            color='neutral'
            className='gap-2xs rounded-full px-xs flex items-center '
          >
            <Image
              src='/images/club-gem.svg'
              alt='coin'
              width={15}
              height={15}
            />
            {/* sidebar profile */}
            <ValueWithUnit value={portfolio.balance ?? 0} unit='square_token' />
          </Badge>
        </Col>
        <Col className='gap-md p-md  border-t border-neutral-surface-5'>
          {accountItems.map((item, index) => {
            const isActive = pathname?.includes(item.href)
            return (
              <Row
                key={index}
                as={Link}
                href={item.href}
                className={cn(
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : '',
                )}
              >
                <Row className='py-2xs gap-md'>
                  <Icon name={item.icon} size='sm' />
                  <Typography size='sm' weight='bold'>
                    {t(item.label)}
                  </Typography>
                </Row>
                {item.badge &&
                  (item.badge.count !== undefined
                    ? item.badge?.count > 0
                    : true) && (
                    <Badge
                      size='sm'
                      variant={item.badge.variant}
                      color={item.badge.color || 'negative'}
                    >
                      {item.badge.count && `${item.badge.count} `}
                      {t(item.badge.text)}
                      {item.badge.value && `${item.badge.value} `}
                    </Badge>
                  )}
                {isActive && (
                  <Icon
                    name='ChevronLeftLine'
                    size='sm'
                    color='fill-neutral-surface-20'
                  />
                )}
              </Row>
            )
          })}
        </Col>
      </Card>
      <Card>
        <Col className='gap-md'>
          {menuItems.map((item, index) => {
            const isActive = pathname?.includes(item.href)
            return (
              <Row
                key={index}
                as={Link}
                href={item.href}
                className={cn(
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : '',
                )}
              >
                <Row className='py-2xs gap-md'>
                  <Icon name={item.icon} size='sm' />
                  <Typography size='sm' weight='bold'>
                    {t(item.label)}
                  </Typography>
                </Row>
                {item.badge &&
                  (item.badge.count !== undefined
                    ? item.badge?.count > 0
                    : true) && (
                    <Badge
                      size='sm'
                      variant={item.badge.variant}
                      color={
                        item.badge.variant === 'solid' ? 'negative' : undefined
                      }
                    >
                      {item.badge.count && `${item.badge.count} `}
                      {t(item.badge.text)}
                      {item.badge.value && `${item.badge.value} `}
                    </Badge>
                  )}
                {isActive && (
                  <Icon
                    name='ChevronLeftLine'
                    size='sm'
                    color='fill-neutral-surface-20'
                  />
                )}
              </Row>
            )
          })}
        </Col>
      </Card>
      <Card>
        <Col className='gap-md'>
          {settingsItems.map((item, index) => {
            const isActive = pathname?.includes(item.href)
            return (
              <Row
                key={index}
                as={Link}
                href={item.href}
                className={cn(
                  'justify-between gap-md',
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : '',
                )}
              >
                <Row className='py-2xs gap-md'>
                  <Icon
                    name={item.icon}
                    size='sm'
                    color='fill-neutral-surface-100'
                  />
                  <Typography size='sm' weight='bold'>
                    {t(item.label)}
                  </Typography>
                </Row>
                {isActive && (
                  <Icon
                    name='ChevronLeftLine'
                    size='sm'
                    color='fill-neutral-surface-20'
                  />
                )}
              </Row>
            )
          })}
        </Col>
      </Card>
      <Card>
        <Col className='gap-md'>
          {supportItems.map((item, index) => {
            const isActive = pathname?.includes(item.href)
            return (
              <Row
                key={index}
                as={Link}
                onClick={item.onClick}
                href={item.onClick ? '#' : item.href}
                className={cn(
                  item.onClick && 'cursor-pointer',
                  'justify-between gap-md',
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : '',
                )}
              >
                <Row className='py-2xs gap-md'>
                  <Icon
                    name={item.icon}
                    size='sm'
                    color='fill-neutral-surface-100'
                  />
                  <Typography size='sm' weight='bold'>
                    {t(item.label)}
                  </Typography>
                </Row>
                {isActive && (
                  <Icon
                    name='ChevronLeftLine'
                    size='sm'
                    color='fill-neutral-surface-20'
                  />
                )}
              </Row>
            )
          })}
        </Col>
      </Card>
      <Button
        width='full'
        variant='transparent'
        startIcon={<Icon name='ExitRightLine' size='sm' />}
        onClick={handleLogout}
        color='neutral'
        className='text-sm'
      >
        {t('common_logout')}
      </Button>
      <Row className='justify-center gap-md mt-md'>
        <Typography weight='regular' size='2xs' color='text-neutral-surface-60'>
          {t('profileLayout_version')} ۰.۰.۰.۰
        </Typography>
      </Row>
    </Col>
  )
}
